/**
 * Renders a complete tab from a TabDef configuration.
 * Non-repeatable fields render as MarcFieldPanel + DynamicFieldPanel.
 * Repeatable fields render as DynamicRepeatableField.
 */
import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button } from '@folio/stripes/components';
import { Controller } from 'react-hook-form';
import { stateKey } from '../config/fieldDefinitionTypes';
import { fieldInstancesHaveContent } from '../utils/fieldInstancesHaveContent';
import MarcFieldPanel from '../fields/MarcFieldPanel';
import IndicatorSelect from '../fields/IndicatorSelect';
import NameTypeIndicatorControl from '../fields/NameTypeIndicatorControl';
import SubfieldInput from '../fields/SubfieldInput';
import DynamicFieldPanel from './DynamicFieldPanel';
import DynamicRepeatableField from './DynamicRepeatableField';
import { resolveLabel } from './labelResolver';
import { indicatorSelectId } from './domIds';
/**
 * Renders indicator selects for a non-repeatable field (instance 0).
 */
var NonRepeatableIndicators = function (_a) {
    var fieldDef = _a.fieldDef, control = _a.control, getValues = _a.getValues, setValue = _a.setValue;
    var hasIndicators = !!(fieldDef.indicator1Def || fieldDef.indicator2Def);
    if (!hasIndicators)
        return null;
    var key = stateKey(fieldDef);
    return (React.createElement(React.Fragment, null,
        fieldDef.indicator1Def && (React.createElement(Controller, { name: "fields.".concat(key, ".0._ind1.value"), control: control, render: function (_a) {
                var field = _a.field;
                // TASK-115: heading-type switch — confirm before clearing data.
                if (fieldDef.indicator1Def.isHeadingTypeSwitch && getValues && setValue) {
                    return (React.createElement(NameTypeIndicatorControl, { fieldDef: fieldDef, indicatorDef: fieldDef.indicator1Def, value: field.value || ' ', onChange: field.onChange, pathPrefix: "fields.".concat(key, ".0"), getValues: getValues, setValue: setValue }));
                }
                return (React.createElement(IndicatorSelect, { indicatorDef: fieldDef.indicator1Def, value: field.value || ' ', onChange: field.onChange, inputId: indicatorSelectId("fields.".concat(key, ".0"), 1) }));
            } })),
        fieldDef.indicator2Def && (React.createElement(Controller, { name: "fields.".concat(key, ".0._ind2.value"), control: control, render: function (_a) {
                var field = _a.field;
                return (React.createElement(IndicatorSelect, { indicatorDef: fieldDef.indicator2Def, value: field.value || ' ', onChange: field.onChange, inputId: indicatorSelectId("fields.".concat(key, ".0"), 2) }));
            } }))));
};
var toggleStyle = {
    marginTop: '4px',
    marginBottom: '16px',
    fontSize: '13px',
};
/**
 * Fields the config marks `defaultVisible: false` (CZNA-343) — prepared, but
 * kept out of the tab body until the cataloger asks for them.
 *
 * A collapsed field that already carries data is rendered unconditionally: `682`
 * is the reason this feature exists, and a cancelled record whose cancellation
 * note is hidden behind a toggle would be a worse form than the pinned empty
 * panel that was the original complaint.
 *
 * Split into its own component so the `watch()` subscription that answers
 * "does it carry data?" re-renders only this section rather than the whole tab
 * on every keystroke.
 */
var CollapsedFields = function (_a) {
    var fields = _a.fields, watch = _a.watch, renderField = _a.renderField, revealFieldTag = _a.revealFieldTag;
    var _b = useState(false), expandedByToggle = _b[0], setExpanded = _b[1];
    // Revealed in the same render, not only from the effect: the jump looks for the
    // panel right after this render commits (CZNA-1451). The effect keeps it open
    // once the jump is over.
    var revealRequested = !!revealFieldTag && fields.some(function (fd) { return fd.tag === revealFieldTag; });
    useEffect(function () {
        if (revealRequested)
            setExpanded(true);
    }, [revealRequested]);
    var expanded = expandedByToggle || revealRequested;
    // Deep paths are not statically resolvable — cast via unknown, as elsewhere
    // in the renderer (see DynamicFieldPanel).
    var watchAny = watch;
    var withContent = new Set();
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        var fieldDef = fields_1[_i];
        var instances = watchAny("fields.".concat(stateKey(fieldDef)));
        if (fieldInstancesHaveContent(fieldDef, instances))
            withContent.add(fieldDef);
    }
    var stillCollapsed = fields.filter(function (fd) { return !withContent.has(fd); });
    // One list in configured order, not "with content" followed by "revealed": the first
    // keystroke into a revealed field gives it content, and moving it between two lists
    // remounted it — focus dropped to <body>, the rest of the typing went nowhere and no blur
    // ever filled its $1 (CZNA-1382).
    return (React.createElement("div", { "data-testid": "collapsed-fields" },
        fields.filter(function (fd) { return expanded || withContent.has(fd); }).map(renderField),
        stillCollapsed.length > 0 && (React.createElement("div", { style: toggleStyle },
            React.createElement(Button, { buttonStyle: "link", onClick: function () { return setExpanded(!expanded); }, "aria-expanded": expanded, "data-testid": "toggle-collapsed-fields", marginBottom0: true }, expanded
                ? React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.hideOptionalFields" })
                : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.showMoreFields", values: { count: stillCollapsed.length } })))))));
};
var DynamicTab = function (_a) {
    var tab = _a.tab, control = _a.control, watch = _a.watch, getValues = _a.getValues, setValue = _a.setValue, showToast = _a.showToast, errors = _a.errors, _b = _a.mode, mode = _b === void 0 ? 'create' : _b, _c = _a.targetRecordAuthorityIdDisplay, targetRecordAuthorityIdDisplay = _c === void 0 ? null : _c, _d = _a.revealFieldTag, revealFieldTag = _d === void 0 ? null : _d;
    var intl = useIntl();
    // TASK-101: in CREATE mode, drop fields flagged as hideOnCreate (e.g., field
    // 001 — authority number is assigned at approval, not at draft save). In
    // UPDATE mode they remain visible so the existing record's value is shown.
    var visibleFields = tab.fields.filter(function (fd) { return !(mode === 'create' && fd.hideOnCreate); });
    /**
     * TASK-101 (review fix): render the `hideOnCreate` field (001) in UPDATE
     * mode as a plain read-only display — no Controller, no `register`, never
     * touches react-hook-form state. The first subfield gets the override
     * value; any other subfields fall back to a disabled empty input. This is
     * intentional so that `genericFormToProposalData` (which iterates RHF
     * `fields` state) cannot synthesize a 001 entry into `proposal_data` on
     * save just because the form rendered the value.
     *
     * CZNA-455: 001 is a control field, so it now arrives with NO subfields at
     * all and the loop below would render an empty panel — the authority number
     * would silently vanish from the update form. A control field gets one value
     * row instead, with an empty `code` so no `$a` is displayed for a subfield
     * MARC 21 does not have.
     */
    var renderReadOnlyDisplayField = function (fieldDef) { return (React.createElement(MarcFieldPanel, { key: stateKey(fieldDef), tag: fieldDef.tag, label: resolveLabel(fieldDef, intl) }, fieldDef.control_field ? (React.createElement(SubfieldInput, { code: "", label: resolveLabel(fieldDef, intl), value: targetRecordAuthorityIdDisplay !== null && targetRecordAuthorityIdDisplay !== void 0 ? targetRecordAuthorityIdDisplay : '', disabled: true })) : fieldDef.subfields.filter(function (sf) { return !sf.hidden; }).map(function (sf, idx) { return (React.createElement(SubfieldInput, { key: sf.code, code: sf.code, label: resolveLabel(sf, intl), value: idx === 0 ? (targetRecordAuthorityIdDisplay !== null && targetRecordAuthorityIdDisplay !== void 0 ? targetRecordAuthorityIdDisplay : '') : '', disabled: true })); }))); };
    var renderField = function (fieldDef) {
        // TASK-101 (review fix): hideOnCreate fields in UPDATE mode use a
        // render-time-only display path (no RHF binding). See note above.
        if (mode === 'update' && fieldDef.hideOnCreate) {
            return renderReadOnlyDisplayField(fieldDef);
        }
        if (fieldDef.repeatable) {
            return (React.createElement(DynamicRepeatableField, { key: stateKey(fieldDef), fieldDef: fieldDef, control: control, watch: watch, getValues: getValues, setValue: setValue, showToast: showToast }));
        }
        var key = stateKey(fieldDef);
        return (React.createElement(MarcFieldPanel, { key: key, tag: fieldDef.tag, label: resolveLabel(fieldDef, intl), headerRight: React.createElement(NonRepeatableIndicators, { fieldDef: fieldDef, control: control, getValues: getValues, setValue: setValue }) },
            React.createElement(DynamicFieldPanel, { fieldDef: fieldDef, pathPrefix: "fields.".concat(key, ".0"), control: control, watch: watch, getValues: getValues, setValue: setValue, showToast: showToast, errors: errors })));
    };
    // CZNA-343: whole-field default visibility. Fields keep their configured
    // order among themselves; the collapsed ones move to the end of the tab,
    // which is where an "and these are also available" affordance belongs.
    var shownFields = visibleFields.filter(function (fd) { return fd.defaultVisible !== false; });
    var collapsedFields = visibleFields.filter(function (fd) { return fd.defaultVisible === false; });
    return (React.createElement("div", null,
        shownFields.map(renderField),
        collapsedFields.length > 0 && (React.createElement(CollapsedFields, { key: tab.id, fields: collapsedFields, watch: watch, renderField: renderField, revealFieldTag: revealFieldTag }))));
};
export default DynamicTab;
